import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Lock, X } from "lucide-react";
import { getCookie, PRICE_NEW, STRIPE_URL } from "../../lib/site";

export const TermsModal = ({ open, onClose }) => {
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChecked(false);
    document.body.style.overflow = "hidden";
    const onEsc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEsc);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[90] flex items-end justify-center p-4 sm:items-center">
          <motion.div
            data-testid="terms-modal-backdrop"
            className="absolute inset-0 bg-choc-deep/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            data-testid="terms-modal"
            className="relative w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl"
            initial={{ opacity: 0, y: 44, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              data-testid="terms-modal-close-button"
              onClick={onClose}
              aria-label="Zamknij"
              className="absolute right-5 top-5 rounded-full p-1.5 text-clay transition-colors duration-300 hover:bg-cream hover:text-choc"
            >
              <X className="h-5 w-5" />
            </button>
            <h3 className="font-display text-2xl font-semibold tracking-tight">Jeszcze jeden krok</h3>
            <p className="mt-2 text-sm leading-relaxed text-clay">
              Zanim przejdziesz do bezpiecznej płatności Stripe, potwierdź
              zapoznanie się z regulaminem.
            </p>
            <label
              data-testid="terms-checkbox-label"
              className="mt-6 flex cursor-pointer items-start gap-3 rounded-2xl border border-sand bg-cream/60 p-4 transition-colors duration-300 hover:border-brick/40"
            >
              <input
                data-testid="terms-checkbox"
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="mt-0.5 h-5 w-5 shrink-0 accent-[#B23A2F]"
              />
              <span className="text-sm leading-relaxed text-choc">
                Oświadczam, że zapoznałem/am się z{" "}
                <Link
                  data-testid="terms-modal-terms-link"
                  to="/regulamin"
                  onClick={(e) => e.stopPropagation()}
                  className="font-medium text-brick underline underline-offset-2"
                >
                  regulaminem sklepu
                </Link>{" "}
                i{" "}
                <Link
                  data-testid="terms-modal-privacy-link"
                  to="/polityka-prywatnosci"
                  onClick={(e) => e.stopPropagation()}
                  className="font-medium text-brick underline underline-offset-2"
                >
                  polityką prywatności
                </Link>{" "}
                i akceptuję ich treść.
              </span>
            </label>
            <button
              data-testid="terms-accept-button"
              disabled={!checked}
              onClick={() => {
                const eventId = crypto.randomUUID();
                if (window.fbq)
                  window.fbq("track", "InitiateCheckout", { value: 219, currency: "PLN" }, { eventID: eventId });
                fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/checkout`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    event_id: eventId,
                    event_source_url: window.location.href,
                    fbp: getCookie("_fbp"),
                    fbc: getCookie("_fbc"),
                  }),
                  keepalive: true,
                }).catch(() => {});
                window.location.href = STRIPE_URL;
              }}
              className="group mt-6 flex w-full items-center justify-center gap-2.5 rounded-full bg-choc px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-choc-deep disabled:cursor-not-allowed disabled:opacity-40"
            >
              Przejdź do płatności — {PRICE_NEW}
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-clay">
              <Lock className="h-3.5 w-3.5" />
              Zostaniesz przekierowany do bezpiecznej płatności Stripe
            </p>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
